import {
    ChevronDown,
    ChevronsUpDown,
    LoaderCircle,
    Plus,
    X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import {
    Badge,
    Button,
    Command,
    CommandEmpty,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuTrigger,
    Label,
    Popover,
    PopoverContent,
    PopoverTrigger,
    Text,
} from '@/ui/design-system/components';
import { views } from '@/copy';
import {
    canonicalExcludeTerms,
    DEFAULT_EXCLUDE_MATCH,
    EXCLUDE_MATCH_MODES,
    formatExcludeTerm,
    MAX_EXCLUDE_TERMS,
    MAX_TERM_LENGTH,
    MIN_CONTAINS_CHARS,
} from '@/ui/components/AiAssistant/visuals/lib/listInputs';
import { SEARCH_DEBOUNCE_MS } from './entitySearch';

export { MAX_EXCLUDE_TERMS };

export function termError(match, value) {
    const term = value.trim();
    if (!term) return null;
    // Code points, not UTF-16 units: the backend's limits are Python len().
    const length = Array.from(term).length;
    if (length > MAX_TERM_LENGTH) {
        return `Too long (${MAX_TERM_LENGTH} characters max).`;
    }
    if (match === 'contains' && length < MIN_CONTAINS_CHARS) {
        return `Needs at least ${MIN_CONTAINS_CHARS} characters.`;
    }
    return null;
}

const ExcludeChip = ({ term, onChangeMatch, onRemove }) => (
    <Badge variant="secondary" className="h-6 gap-1 py-0 pl-0.5 pr-1">
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    aria-label={views.assistantVisuals.excludeMatchTrigger(
                        term.value,
                        views.assistantVisuals.excludeMatchLabels[term.match],
                    )}
                    className="inline-flex cursor-pointer items-center gap-0.5 rounded-sm bg-primary/10 px-1 py-0.5 text-[10px] font-semibold text-primary transition-colors hover:bg-primary/20 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring"
                >
                    {views.assistantVisuals.excludeMatchLabels[term.match]}
                    <ChevronDown className="h-3 w-3" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-32">
                <DropdownMenuRadioGroup
                    value={term.match}
                    onValueChange={onChangeMatch}
                >
                    {EXCLUDE_MATCH_MODES.map((mode) => (
                        <DropdownMenuRadioItem
                            key={mode}
                            value={mode}
                            disabled={Boolean(termError(mode, term.value))}
                        >
                            {views.assistantVisuals.excludeMatchLabels[mode]}
                        </DropdownMenuRadioItem>
                    ))}
                </DropdownMenuRadioGroup>
            </DropdownMenuContent>
        </DropdownMenu>
        <span className="text-xs">{term.value}</span>
        <button
            type="button"
            aria-label={`Remove exclusion ${formatExcludeTerm(term)}`}
            onClick={onRemove}
            className="cursor-pointer rounded-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring"
        >
            <X className="h-3 w-3" />
        </button>
    </Badge>
);

/**
 * Values are lowercased on commit: matching is case-insensitive on both sides,
 * so stray casing would only fork the widget's cache key.
 */
function WidgetExcludeInput({
    value,
    onChange,
    searchNames,
    nounPlural = 'rows',
    id,
    label = 'Exclude',
    maxItems = MAX_EXCLUDE_TERMS,
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [hits, setHits] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const reqIdRef = useRef(0);

    useEffect(() => {
        const q = query.trim();
        if (!q || !searchNames) {
            reqIdRef.current += 1;
            setHits([]);
            setIsSearching(false);
            return undefined;
        }
        setIsSearching(true);
        const reqId = ++reqIdRef.current;
        const timer = setTimeout(async () => {
            try {
                const found = await searchNames(q);
                if (reqId !== reqIdRef.current) return; // stale
                setHits(found ?? []);
            } catch {
                if (reqId === reqIdRef.current) setHits([]);
            } finally {
                if (reqId === reqIdRef.current) setIsSearching(false);
            }
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [query, searchNames]);

    const fieldId = id ?? 'widget-exclude';
    const atLimit = value.length >= maxItems;
    const typed = query.trim().toLowerCase();
    const typedError = termError(DEFAULT_EXCLUDE_MATCH, typed);
    const shownHits = hits.filter((name) => name.toLowerCase() !== typed);

    // Deduped + sorted to match harvestWidgets — order forks the data hash.
    const emit = (terms) => onChange(canonicalExcludeTerms(terms));

    const add = (raw) => {
        const next = raw.trim().toLowerCase();
        if (!next || atLimit || termError(DEFAULT_EXCLUDE_MATCH, next)) return;
        setQuery('');
        if (
            value.some(
                (t) => t.value === next && t.match === DEFAULT_EXCLUDE_MATCH,
            )
        )
            return;
        emit([...value, { match: DEFAULT_EXCLUDE_MATCH, value: next }]);
    };

    const remove = (term) =>
        emit(
            value.filter(
                (t) => !(t.match === term.match && t.value === term.value),
            ),
        );

    const changeMatch = (term, match) =>
        emit(
            value.map((t) =>
                t.match === term.match && t.value === term.value
                    ? { ...t, match }
                    : t,
            ),
        );

    return (
        <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-1">
                <Label
                    htmlFor={fieldId}
                    className="text-sm font-medium text-foreground"
                >
                    {label}
                </Label>
                <Text size="xs" color="muted" className="text-pretty">
                    {views.assistantVisuals.excludeHint(nounPlural)}
                </Text>
            </div>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        id={fieldId}
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        className="w-full justify-between font-normal"
                    >
                        <span
                            className={
                                value.length
                                    ? 'truncate'
                                    : 'text-muted-foreground'
                            }
                        >
                            {value.length
                                ? `${value.length} excluded`
                                : 'Search or type a name…'}
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0" align="start">
                    <Command shouldFilter={false}>
                        <CommandInput
                            placeholder="Search or type a name…"
                            value={query}
                            onValueChange={setQuery}
                        />
                        <CommandList>
                            <CommandEmpty>
                                {isSearching ? (
                                    <span className="flex items-center justify-center gap-2 text-muted-foreground">
                                        <LoaderCircle className="h-4 w-4 animate-spin" />
                                        Searching…
                                    </span>
                                ) : typed ? (
                                    ''
                                ) : (
                                    <span className="block text-balance px-6">
                                        Type a name, or specify one that is not
                                        listed.
                                    </span>
                                )}
                            </CommandEmpty>
                            {shownHits.map((name) => (
                                <CommandItem
                                    key={name}
                                    value={`__hit__${name}`}
                                    disabled={atLimit}
                                    onSelect={() => add(name)}
                                >
                                    <span className="capitalize">{name}</span>
                                </CommandItem>
                            ))}
                            {/* cmdk hides a separator while a search is
                                active unless told otherwise, and this one
                                only ever shows with one. */}
                            {shownHits.length > 0 && typed && !typedError && (
                                <CommandSeparator alwaysRender />
                            )}
                            {typed && !typedError && (
                                <CommandItem
                                    value={`__typed__${typed}`}
                                    disabled={atLimit}
                                    onSelect={() => add(typed)}
                                >
                                    <Plus className="mr-2 h-4 w-4" />
                                    <span>
                                        {views.assistantVisuals.excludeSpecify(
                                            typed,
                                        )}
                                    </span>
                                </CommandItem>
                            )}
                            {typed && typedError && (
                                <div className="px-2 py-1.5 text-xs text-destructive">
                                    {typedError}
                                </div>
                            )}
                            {atLimit && (
                                <div className="px-2 py-1.5 text-xs text-muted-foreground">
                                    Up to {maxItems} exclusions.
                                </div>
                            )}
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
            {value.length > 0 && (
                <div className="flex flex-wrap gap-1">
                    {value.map((term) => (
                        <ExcludeChip
                            key={`${term.match}-${term.value}`}
                            term={term}
                            onChangeMatch={(match) => changeMatch(term, match)}
                            onRemove={() => remove(term)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

export default WidgetExcludeInput;
